import { useApolloClient } from '@apollo/client';
import Button from '@blaze-react/button';
import Input from '@blaze-react/input';
import Select from '@blaze-react/select';
import { useToasts } from '@blaze-react/toaster';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import MessageButtonContent from './MessageButtonContent';
import {
  MESSAGE_TYPE_TO_DATA_LAYER_OPTION,
  MESSAGE_TYPE_TITLES,
  MESSAGE_TYPES,
  PRIVACY
} from '../../constants';
import {
  checkValidEmail,
  pushToDataLayer,
  RESIDENCE_COUNTRIES,
  submitSalesforceNewsletter,
  userRoleOptions
} from '../../utils';
import { buildTransactionId, pushPurchase } from '../../utils/data-layer';
import { postContactMessages } from '../../utils/messages';
import { extractListingDataLayerData } from '../../utils/transformers';

const roleOptions = [['', 'Which category best describes you?'], ...userRoleOptions];

const countryOfResidenceOptions = [
  ['', 'What is your country of residence?'],
  ...RESIDENCE_COUNTRIES.map(country => [country, country])
];

const EcommerceMessageButton = ({
  contacts,
  disableClicks,
  yachtData,
  size,
  messageType,
  buttonText
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [newsletterValues, setNewsletterValues] = useState({
    additionalTermsAccepted: null,
    countryOfResidence: '',
    firstName: '',
    lastName: '',
    email: '',
    role: '',
    termsAccepted: false,
    visible: false
  });
  const [submitState, setSubmitState] = useState(null);
  const [newsletterSubmitState, setNewsletterSubmitState] = useState(null);
  const { addToast } = useToasts();
  const client = useApolloClient();

  const dataLayerPayload = extractListingDataLayerData(yachtData, contacts);

  const openModal = () => {
    if (disableClicks) return;

    setModalOpen(true);
  };

  const closeNewsletterModal = () => {
    if (disableClicks) return;

    setNewsletterValues({
      additionalTermsAccepted: null,
      countryOfResidence: '',
      firstName: '',
      lastName: '',
      email: '',
      role: '',
      termsAccepted: false,
      visible: false
    });
  };

  const newsletterRoleValid = newsletterValues.role && newsletterValues.role !== 'Please choose...';
  const newsletterTermsValid =
    RESIDENCE_COUNTRIES.includes(newsletterValues.countryOfResidence) &&
    !!newsletterValues.termsAccepted;

  const isNewsletterFormFull = !!(
    newsletterValues.additionalTermsAccepted !== null &&
    newsletterValues.firstName &&
    newsletterValues.lastName &&
    newsletterRoleValid &&
    checkValidEmail(newsletterValues.email) &&
    newsletterTermsValid
  );

  const newsletterButtonClass = classnames('newsletter-signup__submit', {
    'newsletter-signup__submit--disabled': !isNewsletterFormFull
  });

  const handleNewsletterChange = ({ target: { name: targetName } }, value) => {
    setNewsletterValues({
      additionalTermsAccepted: newsletterValues.additionalTermsAccepted,
      countryOfResidence: newsletterValues.countryOfResidence,
      firstName: newsletterValues.firstName,
      lastName: newsletterValues.lastName,
      email: newsletterValues.email,
      role: newsletterValues.role,
      termsAccepted: newsletterValues.termsAccepted,
      visible: newsletterValues.visible,
      [targetName]: value
    });
  };

  const handleNewsletterCountryOfResidenceChange = value => {
    setNewsletterValues({
      additionalTermsAccepted: newsletterValues.additionalTermsAccepted,
      countryOfResidence: value,
      firstName: newsletterValues.firstName,
      lastName: newsletterValues.lastName,
      email: newsletterValues.email,
      role: newsletterValues.role,
      termsAccepted: newsletterValues.termsAccepted,
      visible: newsletterValues.visible
    });
  };

  const handleNewsletterTermsCheckboxChange = () => {
    setNewsletterValues({
      additionalTermsAccepted: newsletterValues.additionalTermsAccepted,
      countryOfResidence: newsletterValues.countryOfResidence,
      firstName: newsletterValues.firstName,
      lastName: newsletterValues.lastName,
      email: newsletterValues.email,
      role: newsletterValues.role,
      visible: newsletterValues.visible,
      termsAccepted: !newsletterValues.termsAccepted
    });
  };

  const handleNewsletterAdditionalTermsChange = val => {
    setNewsletterValues({
      additionalTermsAccepted: val,
      countryOfResidence: newsletterValues.countryOfResidence,
      firstName: newsletterValues.firstName,
      lastName: newsletterValues.lastName,
      email: newsletterValues.email,
      role: newsletterValues.role,
      termsAccepted: newsletterValues.termsAccepted,
      visible: newsletterValues.visible
    });
  };

  useEffect(() => {
    const doSubmit = async () => {
      if (newsletterSubmitState === null) return;

      setNewsletterSubmitState(null);

      try {
        const product = 'briefing';

        await submitSalesforceNewsletter(
          newsletterSubmitState.values.firstName,
          newsletterSubmitState.values.lastName,
          newsletterSubmitState.values.email,
          [product],
          {
            country: newsletterSubmitState.values.countryOfResidence,
            additionalTermsAccepted: newsletterSubmitState.values.additionalTermsAccepted
          },
          newsletterSubmitState.values.role
        );

        const basePayload = newsletterSubmitState.values.role
          ? { user: { role: newsletterSubmitState.values.role } }
          : {};
        pushToDataLayer({
          ...basePayload,
          event: 'Newsletter Signup Complete',
          newsletter_name: product
        });

        addToast('Newsletter subscription sent successfully', {
          appearance: 'success',
          autoDismiss: true
        });
      } catch ({ message }) {
        addToast(message, {
          appearance: 'error',
          autoDismiss: true
        });
      }

      setNewsletterValues({
        additionalTermsAccepted: null,
        countryOfResidence: '',
        firstName: '',
        lastName: '',
        email: '',
        role: '',
        termsAccepted: false,
        visible: false
      });
    };
    doSubmit();
  }, [
    addToast,
    newsletterSubmitState,
    pushToDataLayer,
    setNewsletterValues,
    submitSalesforceNewsletter
  ]);

  useEffect(() => {
    const doSubmit = async () => {
      if (submitState === null) return;

      const { formValues, setFormValues } = submitState;
      setSubmitState(null);

      const transactionId = buildTransactionId(yachtData.id);

      let backendFormValues = {
        name: `${formValues.firstName} ${formValues.lastName}`,
        email: formValues.email,
        message: formValues.message,
        messageType
      };

      if (messageType === MESSAGE_TYPES.CALL_BACK) {
        backendFormValues = { ...backendFormValues, phone: formValues.phone };
      }

      const messageSuccessful = await postContactMessages(
        client,
        backendFormValues,
        yachtData,
        contacts,
        addToast,
        transactionId
      );

      if (formValues.newsletter && messageSuccessful) {
        setTimeout(
          () =>
            setNewsletterValues({
              additionalTermsAccepted: null,
              countryOfResidence: formValues.countryOfResidence,
              firstName: formValues.firstName,
              lastName: formValues.lastName,
              email: formValues.email,
              role: '',
              termsAccepted: false,
              visible: true
            }),
          700
        );
      }

      if (messageSuccessful)
        pushPurchase(
          dataLayerPayload,
          MESSAGE_TYPE_TO_DATA_LAYER_OPTION[messageType],
          transactionId
        );

      setFormValues({
        firstName: '',
        lastName: '',
        email: '',
        message: '',
        newsletter: false
      });

      setModalOpen(false);
    };
    doSubmit();
  }, [addToast, client, contacts, dataLayerPayload, messageType, submitState, yachtData]);

  return (
    <>
      <Button
        id={`message-button--${messageType}--${size}`}
        className={`ecommerce__buttons__button ecommerce__buttons__button--message ecommerce__buttons__button--message-${messageType}`}
        title={MESSAGE_TYPE_TITLES[messageType]}
        onClick={openModal}>
        {buttonText || MESSAGE_TYPE_TITLES[messageType]}
      </Button>

      {isModalOpen && (
        <MessageButtonContent
          close={() => setModalOpen(false)}
          contacts={contacts}
          gtmId="broker-form-modal"
          handleSubmit={(formValues, setFormValues) =>
            setSubmitState({ formValues, setFormValues })
          }
          messageType={messageType}
          usePhone={messageType === MESSAGE_TYPES.CALL_BACK}
          yachtData={yachtData}
        />
      )}

      {newsletterValues.visible && (
        <div className="newsletter-signup__modal newsletter-signup__modal--high">
          <div className="newsletter-signup__form">
            <div
              role="button"
              className="newsletter-signup__close"
              onClick={() => closeNewsletterModal()}>
              <i className="fas fa-times" />
            </div>

            <h2 className="newsletter-signup__success">Let's finish your newsletter signup</h2>

            <h3 className="newsletter-signup__subtitle">We've sent your enquiry to the broker</h3>

            <form className="newsletter-signup__body">
              <div className="newsletter-signup__first-and-last-name">
                <Input
                  type="text"
                  name="firstName"
                  placeholder="* First name"
                  className="newsletter-signup__input"
                  required="required"
                  onChange={({ event, value }) => handleNewsletterChange(event, value)}
                  value={newsletterValues.firstName}
                />

                <Input
                  type="text"
                  name="lastName"
                  placeholder="* Last name"
                  className="newsletter-signup__input"
                  required="required"
                  onChange={({ event, value }) => handleNewsletterChange(event, value)}
                  value={newsletterValues.lastName}
                />
              </div>

              <Input
                type="text"
                name="email"
                placeholder="* Email"
                className="newsletter-signup__input"
                required="required"
                onChange={({ event, value }) => handleNewsletterChange(event, value)}
                value={newsletterValues.email}
              />

              <Select
                name="countryOfResidence"
                className="newsletter-signup__input"
                options={countryOfResidenceOptions}
                onChange={({ value }) => handleNewsletterCountryOfResidenceChange(value)}
                selected={newsletterValues.countryOfResidence}
              />

              <Select
                name="role"
                className="newsletter-signup__input"
                options={roleOptions}
                onChange={({ event, value }) => handleNewsletterChange(event, value)}
                selected={newsletterValues.role}
              />

              <div className="newsletter-signup__checkbox">
                <input
                  readOnly
                  className="newsletter-signup__checkbox__box"
                  type="checkbox"
                  checked={newsletterValues.termsAccepted}
                  name="termsAccepted"
                  onClick={() => handleNewsletterTermsCheckboxChange()}
                />

                <label
                  className="newsletter-signup__checkbox__label"
                  onClick={() => handleNewsletterTermsCheckboxChange()}>
                  I agree to the{' '}
                  <a
                    href={PRIVACY.TERMS_HREF}
                    className="boat-pro-form__checkbox__link"
                    target="_blank"
                    rel="noreferrer">
                    terms and conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href={PRIVACY.HREF}
                    className="boat-pro-form__checkbox__link"
                    target="_blank"
                    rel="noreferrer">
                    privacy notice
                  </a>
                  .
                </label>
              </div>

              <div className="newsletter-signup__radio">
                <label className="newsletter-signup__radio__label">
                  I would like to receive tailored information about events and products from BOAT
                  International Media Ltd, an Informa Group Company. You can opt out at any time by
                  clicking unsubscribe or contacting us. For Further details, see our{' '}
                  <a
                    href={PRIVACY.HREF}
                    className="newsletter-signup__checkbox__footer-link"
                    target="_blank"
                    rel="noreferrer">
                    privacy notice
                  </a>
                  .
                </label>

                <div className="newsletter-signup__radio__boxes">
                  <input
                    readOnly
                    type="radio"
                    name="additionalTermsAccepted"
                    value="yes"
                    className="newsletter-signup__radio__box"
                    checked={newsletterValues.additionalTermsAccepted === true}
                    onClick={() => handleNewsletterAdditionalTermsChange(true)}
                  />
                  <label
                    className="newsletter-signup__radio__box-label"
                    onClick={() => handleNewsletterAdditionalTermsChange(true)}>
                    Yes
                  </label>{' '}
                  <input
                    readOnly
                    type="radio"
                    name="additionalTermsAccepted"
                    value="no"
                    className="newsletter-signup__radio__box"
                    checked={newsletterValues.additionalTermsAccepted === false}
                    onClick={() => handleNewsletterAdditionalTermsChange(false)}
                  />
                  <label
                    className="newsletter-signup__radio__box-label"
                    onClick={() => handleNewsletterAdditionalTermsChange(false)}>
                    No
                  </label>
                </div>
              </div>

              <Button
                type="button"
                name="button"
                data-gtm-id="newsletter-signup"
                className={newsletterButtonClass}
                disabled={!isNewsletterFormFull}
                onClick={() =>
                  setNewsletterSubmitState({
                    values: newsletterValues,
                    setValues: setNewsletterValues
                  })
                }>
                Submit
              </Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

EcommerceMessageButton.propTypes = {
  contacts: PropTypes.array.isRequired,
  disableClicks: PropTypes.bool,
  yachtData: PropTypes.object,
  messageType: PropTypes.string.isRequired,
  size: PropTypes.string.isRequired,
  buttonText: PropTypes.string
};

EcommerceMessageButton.defaultProps = {
  disableClicks: false,
  buttonText: ''
};

export default EcommerceMessageButton;
